import { useState, useCallback, useEffect, useMemo } from "react";
import PageHeader from "components/Page/PageHeader";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import { exportExcelRelatorioUnidades } from "./../util/helper";
import useRelatorioUnidadesPdf from "core/features/relatorios/hooks/useRelatorioUnidadesPdf";
import { RelatorioUnidadesFields } from "features/relatorios/typings";
import { TipoUnidadePessoaEnum } from "core/features/unidades/typings/unidade";
import ModalSelectFieldsRelatorio from "ui/components/ModalSelectFieldsRelatorio/index";
import PDFRelatorioUnidades from "./../PDFRelatorio";
import { fieldsModal, extraFieldsModal } from "../util/helper";
import { RelatorioUnidadesPaginadoItem } from "features/relatorios/typings";

import useRelatorioUnidadesInfiniteScroll from "core/features/relatorios/hooks/useRelatorioUnidadesInfiniteScroll";
import RelatorioUnidadeContent from "./../RelatorioUnidadeContent";
import { useLoading } from "components/GlobalLoading";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";
import useFiltroRelatorioUnidades from "../../hooks/useFiltroRelatorioUnidades";
import ModalFiltros from "../ModalFiltros";

export default function ListaRelatorioUnidades() {
  const [exportType, setExportType] = useState<
    { type: "pdf" | "excel" } | undefined
  >();

  const [isOpenModalFiltro, setIsOpenModalFiltro] = useState(false);

  const [modalSelectFieldsOpen, setModalSelectFieldsOpen] = useState(false);

  const { filtro, onChangeFiltro, onResetFiltro } =
    useFiltroRelatorioUnidades();

  const {
    data: dadosRelatorio,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRelatorioUnidadesInfiniteScroll(filtro);

  const { mutate: handleRelatorioUnidades, isLoading: isLoadingPdf } =
    useRelatorioUnidadesPdf(filtro);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Unidades`,
    Component: PDFRelatorioUnidades,
    loading: isLoadingPdf,
  });

  const _onExportPdf = useCallback(
    async ({
      selectedFields,
      tipoUnidadePessoaIds,
      assinaturas,
    }: {
      selectedFields: RelatorioUnidadesFields[];
      tipoUnidadePessoaIds?: TipoUnidadePessoaEnum[];
      assinaturas?: AssinaturasItem[];
    }) => {
      handleRelatorioUnidades(tipoUnidadePessoaIds as any, {
        onSuccess: (pdfData) => {
          onExportPdf({ data: { ...pdfData, assinaturas }, selectedFields });
        },
      });
    },
    [handleRelatorioUnidades, onExportPdf]
  );

  const _onExportExcel = useCallback(
    async ({
      selectedFields,
      tipoUnidadePessoaIds,
    }: {
      selectedFields: RelatorioUnidadesFields[];
      tipoUnidadePessoaIds?: TipoUnidadePessoaEnum[];
    }) => {
      handleRelatorioUnidades(tipoUnidadePessoaIds, {
        onSuccess: (data) => {
          exportExcelRelatorioUnidades({
            items: data.items ?? [],
            includedFields: selectedFields,
          });
        },
      });
    },
    [handleRelatorioUnidades]
  );

  useEffect(() => {
    if (exportType) {
      setModalSelectFieldsOpen(true);
    }
  }, [exportType]);

  const dados = useMemo(() => {
    return (
      dadosRelatorio?.pages.reduce(
        (agg: RelatorioUnidadesPaginadoItem[], item) =>
          agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [dadosRelatorio]);

  useLoading(isLoading);

  const numeroFiltroSelecionados = useMemo(() => {
    return (
      (filtro.gruposUnidadesIds.length ? 1 : 0) +
      (filtro.situacaoAdimplencia ? 1 : 0)
    );
  }, [filtro]);

  return (
    <>
      <PageHeader justifyContent="space-between" title="Unidades" />

      <RelatorioUnidadeContent
        data={dados}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        search={filtro.search}
        setSearch={(s) => onChangeFiltro({ search: s })}
        setExportType={setExportType}
        fetchNextPage={fetchNextPage}
        hasNextPage={!!hasNextPage}
        onFiltro={() => setIsOpenModalFiltro(true)}
        badge={numeroFiltroSelecionados}
      />

      <ModalSelectFieldsRelatorio
        visible={modalSelectFieldsOpen}
        onClose={() => setModalSelectFieldsOpen(false)}
        onSubmit={({ fields, extraFields, assinaturas }) => {
          exportType?.type === "pdf"
            ? _onExportPdf({
                selectedFields: fields,
                tipoUnidadePessoaIds: extraFields as TipoUnidadePessoaEnum[],
                assinaturas,
              })
            : _onExportExcel({
                selectedFields: fields,
                tipoUnidadePessoaIds: extraFields as TipoUnidadePessoaEnum[],
              });

          setModalSelectFieldsOpen(false);
        }}
        fields={fieldsModal}
        extraFieldsTitle="Tipo de pessoa"
        extraFields={extraFieldsModal}
      />

      {isOpenModalFiltro ? (
        <ModalFiltros
          isOpen={isOpenModalFiltro}
          onClose={() => setIsOpenModalFiltro(false)}
          onSave={() => {
            setIsOpenModalFiltro(false);
          }}
          filtro={filtro}
          onChangeFiltro={onChangeFiltro}
          onReset={onResetFiltro}
        />
      ) : null}
    </>
  );
}
